/**
 * AnnotationViewer — renders annotation posts with clickable pins on the source image.
 * Ported from V1 (content-frontend).
 */

import { useState } from 'react'
import { STORAGE_API } from '../config'

interface AnnotationAnchor { x: number; y: number }
interface AnnotationItem {
  id: string
  anchor: AnnotationAnchor
  label: string
  description: string
}
interface AnnotationData {
  object_description?: string
  facts?: (string | { label: string; value: string })[]
  annotations: AnnotationItem[]
}

interface Props {
  content: string
  storageId: number | null
}

export function AnnotationViewer({ content, storageId }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  let data: AnnotationData
  try {
    const parsed = JSON.parse(content)
    data = {
      object_description: parsed.object_description || '',
      facts: parsed.facts || [],
      annotations: parsed.annotations || [],
    }
  } catch (e) {
    return <pre style={{ color: 'var(--color-error)', padding: 20 }}>JSON Parse Error: {String(e)}</pre>
  }

  if (!data.annotations || data.annotations.length === 0) {
    return <p style={{ padding: 20, color: 'var(--text-muted)' }}>Keine Annotationen vorhanden.</p>
  }

  const imageUrl = storageId ? `${STORAGE_API}/storage/media/${storageId}` : null
  const selected = data.annotations.find(a => a.id === selectedId)

  return (
    <div className="annotation-viewer">
      {data.object_description && (
        <p className="annotation-viewer-desc">{data.object_description}</p>
      )}

      {imageUrl && (
        <div className="annotation-viewer-image-wrapper">
          <img src={imageUrl} alt="Annotated" className="annotation-viewer-image" />
          {data.annotations.map(ann => (
            <button
              key={ann.id}
              className={`annotation-pin ${selectedId === ann.id ? 'active' : ''}`}
              style={{ left: `${ann.anchor.x * 100}%`, top: `${ann.anchor.y * 100}%` }}
              onClick={() => setSelectedId(selectedId === ann.id ? null : ann.id)}
              title={ann.label}
            >
              <span className="annotation-pin-dot" />
              <span className="annotation-pin-label">{ann.label}</span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="annotation-detail">
          <h4>{selected.label}</h4>
          <p>{selected.description}</p>
        </div>
      )}

      <div className="annotation-list">
        {data.annotations.map((ann, i) => (
          <div
            key={ann.id}
            className={`annotation-list-item ${selectedId === ann.id ? 'active' : ''}`}
            onClick={() => setSelectedId(selectedId === ann.id ? null : ann.id)}
          >
            <span className="annotation-list-num">{i + 1}</span>
            <div>
              <strong>{ann.label}</strong>
              <p>{ann.description}</p>
            </div>
          </div>
        ))}
      </div>

      {data.facts && data.facts.length > 0 && (
        <div className="annotation-facts">
          <h4>Fakten</h4>
          <ul>
            {data.facts.map((fact, i) => (
              <li key={i}>
                {typeof fact === 'string'
                  ? fact
                  : <><strong>{(fact as {label:string}).label}:</strong> {(fact as {value:string}).value}</>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
